interface Marketplace {
  name: string;
  isDisabled: boolean;
  productUrlTemplate?: string;
  storeUrlTemplate?: string;
  domain?: string;
  logo?: string;
}

interface MarketplaceLinkData {
  productId: string;
  shopId?: string;
  shopName?: string;
  productSlug?: string;
}

// Import types from product module
interface Product {
  id: string;
  sku: string;
  productListingId: string;
  accountId: string;
  marketplace: 'shopee' | 'lazada' | 'tokopedia' | 'tiktok';
  title: string;
  brandName: string;
}

interface Store {
  id: string;
  name: string;
  marketplace: string;
  logo?: string;
  url?: string;
}

export const ListMarketplace: Marketplace[] = [
  { 
    name: "Shopee", 
    isDisabled: false,
    productUrlTemplate: "https://shopee.co.id/product/{shopId}/{productId}",
    storeUrlTemplate: "https://shopee.co.id/shop/{shopId}",
    domain: "shopee.co.id"
  },
  { name: "Shopee_Chat", isDisabled: false },
  { 
    name: "Lazada", 
    isDisabled: false,
    productUrlTemplate: "https://www.lazada.co.id/products/{productSlug}-i{productId}.html",
    storeUrlTemplate: "https://www.lazada.co.id/shop/{shopName}",
    domain: "lazada.co.id"
  },
  { name: "Lazada_Chat", isDisabled: false },
  { 
    name: "Tiktok_Shop", 
    isDisabled: false,
    productUrlTemplate: "https://shop.tiktok.com/view/product/{productId}",
    storeUrlTemplate: "https://shop.tiktok.com/view/shop/{shopId}",
    domain: "shop.tiktok.com"
  },
  { name: "Tiktok_Chat", isDisabled: false },
  { 
    name: "Tokopedia", 
    isDisabled: true,
    productUrlTemplate: "https://www.tokopedia.com/{shopName}/p/{productSlug}",
    storeUrlTemplate: "https://www.tokopedia.com/{shopName}",
    domain: "tokopedia.com"
  },
  { name: "Blibli", isDisabled: true },
  { name: "Bukalapak", isDisabled: true },
  { name: "JDID", isDisabled: true },
  { name: "Bhinneka", isDisabled: true },
  { name: "Orami", isDisabled: true },
];
export const MarketplaceAPI = () => {
  const totalMarketplace = Object.keys("ListMarketplace").length;
  console.log("jumlah marketplace : " + totalMarketplace);
  return totalMarketplace;
};

// Helper function to normalize marketplace names
const normalizeMarketplaceName = (marketplace: string): string => {
  const normalized = marketplace.toLowerCase();
  if (normalized.includes('tiktok')) return 'Tiktok_Shop';
  if (normalized.includes('shopee')) return 'Shopee';
  if (normalized.includes('lazada')) return 'Lazada';
  if (normalized.includes('tokopedia')) return 'Tokopedia';
  return marketplace.charAt(0).toUpperCase() + marketplace.slice(1);
};

// Get marketplace configuration by name
export const getMarketplaceConfig = (marketplace: string): Marketplace | undefined => {
  const normalizedName = normalizeMarketplaceName(marketplace);
  return ListMarketplace.find(m => m.name === normalizedName);
};

// Create product slug from product title
const createProductSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .trim();
};

// Generate marketplace-specific product URL
export const generateMarketplaceProductLink = (marketplace: string, product: Product): string => {
  const config = getMarketplaceConfig(marketplace);
  
  if (!config || config.isDisabled || !config.productUrlTemplate) {
    console.warn(`Marketplace ${marketplace} is not supported or disabled`);
    return `https://marketplace-fallback.com/product/${product.id}`;
  }

  const productSlug = createProductSlug(product.title);
  
  let url = config.productUrlTemplate;
  url = url.replace('{productId}', product.productListingId || product.id);
  url = url.replace('{shopId}', product.accountId);
  url = url.replace('{productSlug}', productSlug);
  
  return url;
};

// Generate marketplace-specific store URL
export const generateMarketplaceStoreLink = (marketplace: string, product: Product, store?: Store): string => {
  const config = getMarketplaceConfig(marketplace);
  
  if (!config || config.isDisabled || !config.storeUrlTemplate) {
    console.warn(`Marketplace ${marketplace} is not supported or disabled`);
    return `https://marketplace-fallback.com/store/${product.accountId}`;
  }

  const shopName = store?.name ? createProductSlug(store.name) : product.brandName;
  
  let url = config.storeUrlTemplate;
  url = url.replace('{shopId}', product.accountId);
  url = url.replace('{shopName}', shopName);
  
  return url;
};

// Copy product link to clipboard with notification
export const copyMarketplaceProductLink = async (marketplace: string, product: Product): Promise<boolean> => {
  try {
    const url = generateMarketplaceProductLink(marketplace, product);
    await navigator.clipboard.writeText(url);
    
    // Dynamic import to avoid dependency issues in tests
    const { toast } = await import('react-toastify');
    toast.success('Product link copied to clipboard!', {
      position: "top-right",
      autoClose: 3000,
    });
    
    return true;
  } catch (error) {
    console.error('Failed to copy product link:', error);
    
    // Dynamic import to avoid dependency issues in tests
    const { toast } = await import('react-toastify');
    toast.error('Failed to copy product link. Please try again.', {
      position: "top-right",
      autoClose: 4000,
    });
    
    return false;
  }
};

// Copy store link to clipboard with notification
export const copyMarketplaceStoreLink = async (marketplace: string, product: Product, store?: Store): Promise<boolean> => {
  try {
    const url = generateMarketplaceStoreLink(marketplace, product, store);
    await navigator.clipboard.writeText(url);
    
    // Dynamic import to avoid dependency issues in tests
    const { toast } = await import('react-toastify');
    toast.success('Store link copied to clipboard!', {
      position: "top-right",
      autoClose: 3000,
    });
    
    return true;
  } catch (error) {
    console.error('Failed to copy store link:', error);
    
    // Dynamic import to avoid dependency issues in tests
    const { toast } = await import('react-toastify');
    toast.error('Failed to copy store link. Please try again.', {
      position: "top-right",
      autoClose: 4000,
    });
    
    return false;
  }
};

// Open marketplace product page
export const openMarketplaceProduct = (marketplace: string, product: Product): void => {
  const url = generateMarketplaceProductLink(marketplace, product);
  window.open(url, '_blank', 'noopener,noreferrer');
};

// Open marketplace store page
export const openMarketplaceStore = (marketplace: string, product: Product, store?: Store): void => {
  const url = generateMarketplaceStoreLink(marketplace, product, store);
  window.open(url, '_blank', 'noopener,noreferrer');
};

// Validate if a URL belongs to a specific marketplace
export const isValidMarketplaceUrl = (url: string, marketplace: string): boolean => {
  const config = getMarketplaceConfig(marketplace);
  if (!config || !config.domain) return false;
  
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.includes(config.domain);
  } catch {
    return false;
  }
};

// Get marketplace display name and formatting
export const getMarketplaceDisplayInfo = (marketplace: string) => {
  const config = getMarketplaceConfig(marketplace);
  return {
    name: config?.name || marketplace,
    isEnabled: config ? !config.isDisabled : false,
    domain: config?.domain,
    hasProductLink: !!(config?.productUrlTemplate),
    hasStoreLink: !!(config?.storeUrlTemplate),
  };
};

export default Marketplace;
